import * as PIXI from "pixi.js";
import { Spine } from "@pixi/spine-pixi";
import { gsap } from "gsap";
import { soundManager } from "./shared/UI/SoundManager";

// Create a SymbolPool class to manage the pool of symbols
class SymbolPool {
  private pool: Symbol[] = [];

  constructor(initialPoolSize: number = 10) {
    for (let i = 0; i < initialPoolSize; i++) {
      this.pool.push(new Symbol("1"));
    }
  }

  public acquire(): Symbol {
    let symbol: Symbol;
    if (this.pool.length > 0) {
      symbol = this.pool.pop()!;
    } else {
      symbol = new Symbol("1");
    }

    symbol.alpha = 1;
    symbol.scale.set(1);
    symbol.position.set(0, 0);

    return symbol;
  }

  public release(symbol: Symbol) {
    symbol.parent?.removeChild(symbol);
    // @ts-ignore - access internal state to wipe listeners
    symbol["symbol"].state.clearListeners();
    this.pool.push(symbol);
  }
}

export class Symbol extends PIXI.Container {
  public static playedExpandingWildAudio = false;
  public static playedAppearingWildAudio = false;

  private isWinAnimating: boolean = false;

  private type: string | undefined;
  private symbol: Spine;
  private multiplier: number = 0;

  public originalPosition!: PIXI.Point;
  public originalParent!: PIXI.Container;

  public static SYMBOL_WIDTH: number = 145; // Default width of the Symbol.
  public static SYMBOL_HEIGHT: number = 140; // Default height of the Symbol.

  private static _pool: SymbolPool;

  public static get pool(): SymbolPool {
    if (!Symbol._pool) {
      throw new Error(
        "Symbol pool is not initialized. Call Symbol.createPool() first.",
      );
    }
    return Symbol._pool;
  }

  public static createPool(initialPoolSize: number = 10) {
    Symbol._pool = new SymbolPool(initialPoolSize);
  }

  constructor(type: string) {
    super();
    this.symbol = Spine.from({
      skeleton: "sevenJson",
      atlas: "sevenAtlas",
    });

    this.addChild(this.symbol);
    this.setType(type.toString());
  }

  getType() {
    return this.type;
  }

  getMultiplier() {
    return this.multiplier;
  }

  setType(type: string) {
    this.isWinAnimating = false;

    this.removeChild(this.symbol);

    this.symbol.destroy();
    this.type = type;
    let symbolName: string = "";
    this.zIndex = 0;
    switch (this.type) {
      case "1":
        symbolName = "seven";
        break;
      case "2":
        symbolName = "l_grape";
        break;
      case "3":
        symbolName = "l_wmelon";
        break;
      case "4":
        symbolName = "bell";
        break;
      case "5":
        symbolName = "l_plum";
        break;
      case "6":
        symbolName = "l_orange";
        break;
      case "7":
        symbolName = "l_lemon";
        break;
      case "8":
        symbolName = "l_cherry";
        break;
      case "100":
        symbolName = "dollar";
        break;
      case "101":
        symbolName = "star";
        break;
      case "200":
        symbolName = "wild";
        this.zIndex = 10;
        break;
      default:
        console.error("Invalid symbol type:" + type);
        break;
    }

    if (symbolName[0] === "l") {
      this.symbol = Spine.from({
        skeleton: "lowsJson",
        atlas: "lowsAtlas",
      });
      this.symbol.skeleton.setSkinByName(symbolName);
    } else {
      this.symbol = Spine.from({
        skeleton: `${symbolName}Json`,
        atlas: `${symbolName}Atlas`,
      });
    }
    this.symbol.skeleton.setSlotsToSetupPose();
    this.symbol.scale.set(0.11);
    this.symbol.angle = 0;

    this.addChild(this.symbol);
    this.idle();

    this.symbol.state.clearListeners();

    this.symbol.state.addListener({
      event: function(entry, event) { },
      complete: (entry) => {
        if (entry.animation) {
          this.emit(entry.animation.name);
        }
      },
      start: (entry) => {
        if (entry.animation!.name.includes("land") && this.type === "200") {
          console.log(`playing land audio on symbol ${this.type}`);
          soundManager.playSound("appearingWild");
        }

        if (entry.animation!.name.includes("land") && this.type === "200") {
          if (!Symbol.playedAppearingWildAudio) {
            console.log(`playing land audio on symbol ${this.type}`);
            soundManager.playSound("appearingWild");
            Symbol.playedAppearingWildAudio = true;
          }
        }

        if (!Symbol.playedExpandingWildAudio) {
          if (entry.animation!.name.includes("expand")) {
            console.log(`playing expand audio on symbol ${this.type}`);
            soundManager.playSound("expandingWild");
          }

          Symbol.playedExpandingWildAudio = true;
        }

      },
      end: function(entry) { },
      dispose: function(entry) { },
    });
  }

  public async win(quickStop?: boolean): Promise<void> {
    if (this.isWinAnimating) return Promise.resolve();

    this.isWinAnimating = true;

    return new Promise((resolve) => {
      const animationName = this.type === "200" ? "match_expand" : "match";
      const idleName = this.type === "200" ? "idle_expand" : "idle";

      // 1. Create a stable listener object
      const listener = {
        complete: (entry: any) => {
          // console.log(`completed animation ${animationName}`);
          if (entry.animation.name === animationName) {
            // 2. Remove using the exact object reference
            this.symbol.state.removeListener(listener);
            this.isWinAnimating = false;
            resolve();
          }
        }
      };

      this.symbol.state.addListener(listener);
      this.symbol.state.setAnimation(0, animationName, false);
      this.symbol.state.addAnimation(0, idleName, true, 0);

      if (quickStop) {
        this.symbol.state.removeListener(listener); // Clean up immediately
        this.isWinAnimating = false;
        resolve();
      }
    });
  }

  public expandWild() {
    return new Promise<void>((resolve) => {
      if (this.type === "200") {
        this.once("expand", () => {
          this.symbol.state.setAnimation(0, "idle_expand", true);
          resolve();
        });
        this.symbol.state.setAnimation(0, "expand", false);
      } else {
        resolve();
      }
    });
  }

  public hideSymbol(): Promise<void> {
    return new Promise((resolve) => {

      gsap.to(this, {
        alpha: 0,
        duration: 0.1,
        onComplete: () => {
          resolve();
        },
      });
    });
  }

  public land() {
    this.symbol.state.setAnimation(0, "land", false);
    this.symbol.state.addAnimation(0, "idle", true);
  }

  public idle() {
    let animationName = "idle";
    if (this.type === "10") {
      animationName = "m_idle_on";
    }
    // Get the animation duration (in frames or time)
    const animation = this.symbol.skeleton.data.findAnimation(animationName);
    if (animation) {
      const duration = animation.duration;

      // Randomly pick a time between 0 and the animation duration
      const randomTime = Math.random() * duration;

      // Set the animation to play from the random time
      this.symbol.state.setAnimation(0, animationName, true).timeScale = 0.5; // loop is set to true

      this.symbol.state.update(randomTime);
    }
  }
}
